import { useMemo, useState, useRef, useCallback, useId } from 'react';
import type { RaceFilter } from '../../types';
import {
  DATE_PRESETS,
  type DatePresetKey,
  getDateRangeForPreset,
  getActiveDatePreset,
} from '../../utils/dateRange';
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss';
import './DateFilterPopover.css';

interface DateFilterPopoverProps {
  startDate?: string;
  endDate?: string;
  onDateRangeChange: (range: Pick<RaceFilter, 'startDate' | 'endDate'>) => void;
}

export function DateFilterPopover({ startDate, endDate, onDateRangeChange }: DateFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const activePreset = useMemo(
    () => getActiveDatePreset(startDate, endDate),
    [startDate, endDate],
  );
  const activeLabel =
    activePreset != null
      ? DATE_PRESETS.find((p) => p.key === activePreset)?.label
      : null;

  usePopoverDismiss(open, wrapperRef, useCallback(() => setOpen(false), []));

  const selectPreset = (key: DatePresetKey) => {
    if (activePreset === key) {
      onDateRangeChange({ startDate: undefined, endDate: undefined });
      return;
    }
    onDateRangeChange(getDateRangeForPreset(key));
  };

  const clear = () => onDateRangeChange({ startDate: undefined, endDate: undefined });

  return (
    <div className="date-popover-wrapper" ref={wrapperRef}>
      <button
        className={`filter-trigger ${activePreset ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={popoverId}
      >
        <span className="filter-trigger-label">기간</span>
        {activeLabel && <span className="filter-trigger-value">{activeLabel}</span>}
        <span className={`filter-trigger-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="filter-popover date-popover" id={popoverId} role="dialog" aria-label="기간 필터">
          <div className="filter-popover-header">
            <span className="filter-popover-title eyebrow">기간 선택</span>
            {activePreset && (
              <button className="filter-popover-clear" onClick={clear}>
                초기화
              </button>
            )}
          </div>
          <ul className="filter-popover-list date-popover-list">
            {DATE_PRESETS.map((preset) => {
              const selected = activePreset === preset.key;
              return (
                <li key={preset.key} className="date-preset-item">
                  <button
                    type="button"
                    className={`date-preset-btn ${selected ? 'selected' : ''}`}
                    onClick={() => selectPreset(preset.key)}
                    aria-pressed={selected}
                  >
                    <span className="date-preset-label display">{preset.label}</span>
                    <span className="date-preset-sub">{preset.sub}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
